import { host } from '../script/variables';

// Thin fetch wrapper for the admin plan endpoints (cookie-based admin session)
const request = async (path, { method = 'GET', body } = {}) => {
  const res = await fetch(`${host}/admin${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = {};
  try { data = await res.json(); } catch { /* empty body */ }
  if (!res.ok) {
    const err = new Error(data.msg || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

const qs = (params) => {
  const s = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')).toString();
  return s ? `?${s}` : '';
};

export const planApi = {
  registry:        ()                 => request('/plans/registry'),
  listPlans:       ()                 => request('/plans'),
  createPlan:      (body)             => request('/plans', { method: 'POST', body }),
  getPlan:         (planId)           => request(`/plans/${planId}`),
  updatePlan:      (planId, body)     => request(`/plans/${planId}`, { method: 'PATCH', body }),
  createVersion:   (planId, body)     => request(`/plans/${planId}/versions`, { method: 'POST', body }),
  updateVersion:   (planId, v, body)  => request(`/plans/${planId}/versions/${v}`, { method: 'PUT', body }),
  deleteVersion:   (planId, v)        => request(`/plans/${planId}/versions/${v}`, { method: 'DELETE' }),
  publishVersion:  (planId, v)        => request(`/plans/${planId}/versions/${v}/publish`, { method: 'POST' }),
  archiveVersion:  (planId, v)        => request(`/plans/${planId}/versions/${v}/archive`, { method: 'POST' }),

  listSubscriptions: (params)         => request(`/subscriptions${qs(params)}`),
  subscriptionStats: ()               => request('/subscriptions/stats'),
  searchOwners:      (q)              => request(`/subscriptions/owners/search${qs({ q })}`),
  getSubscription:   (id)             => request(`/subscriptions/${id}`),
  assign:            (body)           => request('/subscriptions', { method: 'POST', body }),
  changePlan:        (id, body)       => request(`/subscriptions/${id}/change-plan`, { method: 'POST', body }),
  cancelScheduled:   (id)             => request(`/subscriptions/${id}/scheduled-change`, { method: 'DELETE' }),
  changeStatus:      (id, body)       => request(`/subscriptions/${id}/status`, { method: 'POST', body }),
  renew:             (id, body)       => request(`/subscriptions/${id}/renew`, { method: 'POST', body }),
  cancelAtPeriodEnd: (id, body)       => request(`/subscriptions/${id}/cancel-at-period-end`, { method: 'POST', body }),
  updateNotes:       (id, notes)      => request(`/subscriptions/${id}/notes`, { method: 'PATCH', body: { notes } }),
  addOverride:       (id, body)       => request(`/subscriptions/${id}/overrides`, { method: 'POST', body }),
  removeOverride:    (id, overrideId) => request(`/subscriptions/${id}/overrides/${overrideId}`, { method: 'DELETE' }),
  runMaintenance:    ()               => request('/subscriptions/maintenance/run', { method: 'POST' }),

  listPayments:      (params)         => request(`/subscription-payments${qs(params)}`),

  billingSettings:       ()             => request('/billing/settings'),
  updateBillingSettings: (body)         => request('/billing/settings', { method: 'PUT', body }),
  gateReport:            (days)         => request(`/billing/gate-report${qs({ days })}`),
  billingJobs:           ()             => request('/billing/jobs'),
  runBillingJob:         (name)         => request(`/billing/jobs/${encodeURIComponent(name)}/run`, { method: 'POST' }),
  backfill:              (body)         => request('/billing/backfill', { method: 'POST', body }),
  usageList:             (params)       => request(`/billing/usage${qs(params || {})}`),
  usageDetail:           (ownerId, live) => request(`/billing/usage/${encodeURIComponent(ownerId)}${qs({ live: live ? 1 : undefined })}`),
  usageRecount:          (ownerId)      => request(`/billing/usage/${encodeURIComponent(ownerId)}/recount`, { method: 'POST' }),
  reconcilePayment:  (id)             => request(`/subscription-payments/${id}/reconcile`, { method: 'POST' }),
  markRefunded:      (id, note)       => request(`/subscription-payments/${id}/mark-refunded`, { method: 'POST', body: { note } }),
};

// ─── Formatting helpers ─────────────────────────────────────────────────────
export const formatMoney = (minor, currency = 'INR') => {
  if (minor === null || minor === undefined || minor === '') return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: minor % 100 ? 2 : 0 }).format(minor / 100);
};

// Rupees (string/number from an input) → paise integer
export const toMinor = (rupees) => {
  if (rupees === '' || rupees === null || rupees === undefined) return '';
  return Math.round(Number(rupees) * 100);
};

export const fromMinor = (minor) => (minor === null || minor === undefined || minor === '' ? '' : minor / 100);

export const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
export const formatDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

export const INTERVAL_LABELS = { month: 'Monthly', quarter: 'Quarterly', half_year: 'Half-yearly', year: 'Yearly' };
export const STATUS_LABELS = {
  pending_payment: 'Pending payment', trialing: 'Trialing', active: 'Active', past_due: 'Past due',
  grace_period: 'Grace period', suspended: 'Suspended', cancelled: 'Cancelled', expired: 'Expired',
  draft: 'Draft', published: 'Published', archived: 'Archived', retired: 'Retired',
};
export const STATUS_TONE = {
  active: 'good', trialing: 'info', published: 'good', pending_payment: 'warn', past_due: 'warn',
  grace_period: 'warn', draft: 'warn', suspended: 'bad', cancelled: 'muted', expired: 'muted', archived: 'muted', retired: 'muted',
};

export const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${+(bytes / 1024 ** i).toFixed(2)} ${units[i]}`;
};
